import clsx from 'clsx'
import { useImperativeHandle, type ComponentPropsWithRef, type ReactElement } from 'react'

import { useIntersectionObserver } from '@lib/hooks/use-intersection-observer'

import styles from './video.module.css'

export type Source = {
  id: string | number
  url: string
  type: string
}

export interface VideoProps extends ComponentPropsWithRef<'video'> {
  lazyLoad?: boolean
  sources: Source[]
  onVideoEnded?: (loop: boolean) => void
}

export function Video(props: VideoProps): ReactElement {
  const {
    className,
    loop = false,
    lazyLoad = false,
    ref,
    sources,
    onVideoEnded,
    poster,
    ...rest
  } = props

  const [videoRef, isIntersecting] = useIntersectionObserver<HTMLVideoElement>({
    enterThreshold: 0.5,
    freezeOnceVisible: true,
  })

  /**
   * This allows the parent ref to control the video,
   * while allowing the ref in this file to be used for
   * intersection observer.
   */
  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement)

  const loadingIsEager = !lazyLoad
  const elementIsVisible = lazyLoad && isIntersecting
  const loadSources = (loadingIsEager || elementIsVisible) && sources?.length > 0
  // Allows lazy loading to be faked on the poster
  const loadPoster = (loadingIsEager || elementIsVisible) && poster

  /**
   * Sometimes the parent component will want to handle
   * what happens when/how the video loops. This allows
   * passing `loop` as a prop, while also not overriding
   * the parent handling the video loop.
   */
  const loopingHandledInternally = !onVideoEnded && loop

  return (
    <video
      ref={videoRef}
      className={clsx(styles.video, className)}
      onEnded={() => onVideoEnded?.(loop)}
      loop={loopingHandledInternally}
      poster={loadPoster && poster !== '' ? poster : undefined}
      {...rest}
    >
      {loadSources && sources.map(s => s.url && <source key={s.id} src={s.url} type={s.type} />)}
      Sorry, your browser doesn't support embedded videos.
    </video>
  )
}
